import { z } from 'zod';
import { selfDisclosureContractLabels, selfDisclosureContracts, selfDisclosureEmploymentLabels, selfDisclosureEmployments, selfDisclosureHousingSituationLabels, selfDisclosureHousingSituations, selfDisclosureMaritalStatusLabels, selfDisclosureMaritalStatuses, selfDisclosureOtherIncomeTypeLabels, selfDisclosureOtherIncomeTypes, } from './api/self-disclosure.js';
import { formatDate, formatEuro, formatInteger, formatPercent } from './format.js';
export const contactProfileFieldKeys = [
    // person (columns of `contact` + the primary phone/e-mail entries)
    'salutation',
    'firstName',
    'lastName',
    'bornOn',
    'street',
    'postalCode',
    'city',
    'phone',
    'email',
    // household
    'maritalStatus',
    'children',
    'nationality',
    // income
    'employment',
    'employer',
    'employedSinceOn',
    'contract',
    'fixedTermUntilOn',
    'netIncomeCents',
    'salariesPerYear',
    'specialPaymentsPaCents',
    'otherIncomeCents',
    'otherIncomeType',
    'rentalIncomeCents',
    'taxRatePct',
    // expenses
    'housingSituation',
    'grossRentCents',
    'rentContinues',
    'loansCents',
    'loansRemainingTerm',
    'alimonyCents',
    'privateHealthInsuranceCents',
    'leasingCents',
    // assets
    'equityCents',
    'securitiesCents',
    'otherAssetsCents',
    // lead (D-341: what the lead gave in the form, mapped by the lead ingestion)
    'leadIncomeBand',
    'leadEquityAvailability',
    'leadEquityBand',
    'reachability',
    'leadAnswers',
    // acquisition
    'sourceChannel',
    'campaign',
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'receivedAt',
    'firstAppointmentBookedAt',
    'firstAppointmentAt',
    'firstSetter',
    'firstBookedVia',
    'firstAppointmentOwner',
];
export const contactPropertySources = [
    'self_disclosure',
    'manual',
    'appointment',
    'backfill',
    // 0044 (PKV)
    'health_questionnaire',
    'calculator',
    'pkv_application',
    'payslip',
    // 0050 (lead form answers)
    'lead',
];
export const CONTACT_PROFILE_GROUPS = [
    { key: 'person', label: 'Person', sensitive: false },
    { key: 'household', label: 'Haushalt', sensitive: false },
    { key: 'income', label: 'Beruf & Einkommen', sensitive: true },
    { key: 'expenses', label: 'Ausgaben', sensitive: true },
    { key: 'assets', label: 'Vermögen', sensitive: true },
    { key: 'lead', label: 'Lead-Angaben', sensitive: true },
    { key: 'acquisition', label: 'Herkunft & Terminierung', sensitive: false },
];
/** „Gebucht über" of an appointment (Q-08/Q-09, appointment_booked_via). */
export const appointmentBookedViaLabels = {
    internal: 'Intern',
    booking_page: 'Buchungsseite, selbst gebucht',
    dialer: 'Dialer',
    portal: 'Mandantenportal',
    chatbot: 'Chatbot',
    rebooking: 'Umbuchung',
};
function options(labels, values) {
    return values.map((value) => ({ value, label: labels[value] ?? value }));
}
const SENSITIVE_GROUPS = new Set([
    'income',
    'expenses',
    'assets',
    'lead',
]);
/** Keys only the lead ingestion writes (shown read-only, never edited by hand). */
const LEAD_ONLY_KEYS = new Set(['leadAnswers']);
function field(key, label, group, type, extra = {}) {
    return {
        key,
        label,
        group,
        type,
        ...(extra.options ? { options: extra.options } : {}),
        editable: group !== 'acquisition' && !LEAD_ONLY_KEYS.has(key),
        sensitive: SENSITIVE_GROUPS.has(group),
    };
}
export const CONTACT_PROFILE_FIELDS = [
    field('salutation', 'Anrede', 'person', 'text'),
    field('firstName', 'Vorname', 'person', 'text'),
    field('lastName', 'Nachname', 'person', 'text'),
    field('bornOn', 'Geburtsdatum', 'person', 'date'),
    field('street', 'Straße', 'person', 'text'),
    field('postalCode', 'PLZ', 'person', 'text'),
    field('city', 'Ort', 'person', 'text'),
    field('phone', 'Telefon', 'person', 'text'),
    field('email', 'E-Mail', 'person', 'text'),
    field('maritalStatus', 'Familienstand', 'household', 'enum', {
        options: options(selfDisclosureMaritalStatusLabels, selfDisclosureMaritalStatuses),
    }),
    field('children', 'Kinder', 'household', 'integer'),
    field('nationality', 'Staatsangehörigkeit', 'household', 'text'),
    field('employment', 'Beschäftigung', 'income', 'enum', {
        options: options(selfDisclosureEmploymentLabels, selfDisclosureEmployments),
    }),
    field('employer', 'Arbeitgeber', 'income', 'text'),
    field('employedSinceOn', 'Beschäftigt seit', 'income', 'date'),
    field('contract', 'Vertrag', 'income', 'enum', {
        options: options(selfDisclosureContractLabels, selfDisclosureContracts),
    }),
    field('fixedTermUntilOn', 'Befristet bis', 'income', 'date'),
    field('netIncomeCents', 'Nettoeinkommen mtl.', 'income', 'cents'),
    field('salariesPerYear', 'Gehälter pro Jahr', 'income', 'integer'),
    field('specialPaymentsPaCents', 'Sonderzahlungen p. a.', 'income', 'cents'),
    field('otherIncomeCents', 'Sonstiges Einkommen mtl.', 'income', 'cents'),
    field('otherIncomeType', 'Art sonstiges Einkommen', 'income', 'enum', {
        options: options(selfDisclosureOtherIncomeTypeLabels, selfDisclosureOtherIncomeTypes),
    }),
    field('rentalIncomeCents', 'Mieteinnahmen mtl.', 'income', 'cents'),
    field('taxRatePct', 'Persönlicher Steuersatz', 'income', 'percent'),
    field('housingSituation', 'Wohnsituation', 'expenses', 'enum', {
        options: options(selfDisclosureHousingSituationLabels, selfDisclosureHousingSituations),
    }),
    field('grossRentCents', 'Warmmiete mtl.', 'expenses', 'cents'),
    field('rentContinues', 'Miete läuft weiter', 'expenses', 'boolean'),
    field('loansCents', 'Kredite mtl.', 'expenses', 'cents'),
    field('loansRemainingTerm', 'Restlaufzeit Kredite', 'expenses', 'text'),
    field('alimonyCents', 'Unterhalt mtl.', 'expenses', 'cents'),
    field('privateHealthInsuranceCents', 'Private Krankenversicherung mtl.', 'expenses', 'cents'),
    field('leasingCents', 'Leasing mtl.', 'expenses', 'cents'),
    field('equityCents', 'Eigenkapital', 'assets', 'cents'),
    field('securitiesCents', 'Wertpapiere', 'assets', 'cents'),
    field('otherAssetsCents', 'Sonstiges Vermögen', 'assets', 'cents'),
    field('leadIncomeBand', 'Einkommen laut Lead', 'lead', 'text'),
    field('leadEquityAvailability', 'Eigenkapital einsetzbar', 'lead', 'text'),
    field('leadEquityBand', 'Eigenkapital laut Lead', 'lead', 'text'),
    field('reachability', 'Erreichbarkeit', 'lead', 'text'),
    field('leadAnswers', 'Weitere Angaben', 'lead', 'text'),
    field('sourceChannel', 'Quelle', 'acquisition', 'text'),
    field('campaign', 'Kampagne', 'acquisition', 'text'),
    field('utmSource', 'UTM Source', 'acquisition', 'text'),
    field('utmMedium', 'UTM Medium', 'acquisition', 'text'),
    field('utmCampaign', 'UTM Campaign', 'acquisition', 'text'),
    field('receivedAt', 'Eingegangen', 'acquisition', 'date'),
    field('firstAppointmentBookedAt', 'Erster Termin gebucht am', 'acquisition', 'date'),
    field('firstAppointmentAt', 'Erster Termin am', 'acquisition', 'date'),
    field('firstSetter', 'Terminiert von', 'acquisition', 'text'),
    field('firstBookedVia', 'Gebucht über', 'acquisition', 'enum', {
        options: options(appointmentBookedViaLabels, Object.keys(appointmentBookedViaLabels)),
    }),
    field('firstAppointmentOwner', 'Erster Termin bei', 'acquisition', 'text'),
];
const FIELD_BY_KEY = new Map(CONTACT_PROFILE_FIELDS.map((entry) => [entry.key, entry]));
export function contactProfileField(key) {
    const entry = FIELD_BY_KEY.get(key);
    if (!entry)
        throw new Error(`Unknown contact profile field ${key}`);
    return entry;
}
export function isContactProfileFieldKey(key) {
    return FIELD_BY_KEY.has(key);
}
/** Keys of the financial groups incl. „Lead-Angaben" (hidden for referrer and developer_readonly, read-only for setters). */
export const contactProfileSensitiveKeys = CONTACT_PROFILE_FIELDS.filter((entry) => entry.sensitive).map((entry) => entry.key);
/** Keys of the acquisition snapshot (write-once, system only). */
export const contactProfileLockedKeys = CONTACT_PROFILE_FIELDS.filter((entry) => entry.group === 'acquisition').map((entry) => entry.key);
export const CONTACT_PROFILE_LOCKED_MESSAGE = 'Wird beim ersten Termin festgeschrieben.';
/** Answer to a manual change of „Weitere Angaben" (written by the lead ingestion only, D-341). */
export const CONTACT_PROFILE_LEAD_ONLY_MESSAGE = 'Wird aus den Lead-Angaben übernommen.';
/** The label of the property source of the lead ingestion (field_sources, versions, timeline). */
export const CONTACT_PROFILE_LEAD_SOURCE_LABEL = 'Lead';
/**
 * Suggested values of the free-text lead properties (the normalised values of the lead ingestion; any other text is
 * allowed when editing by hand).
 */
export const CONTACT_PROFILE_LEAD_SUGGESTIONS = {
    leadIncomeBand: ['unter 3.000 € netto', '3.000–4.000 € netto', 'über 3.500 € netto', 'über 4.000 € netto'],
    leadEquityAvailability: [
        'Ja, Teil einsetzbar',
        'Ja, voll einsetzbar',
        'Ja, nicht einsetzbar',
        'Nein (Monat zu Monat)',
    ],
    leadEquityBand: ['unter 15.000 €', '10.000–30.000 €', '15.000–30.000 €', 'über 30.000 €'],
    reachability: [
        'morgens bis 8 Uhr',
        'vormittags bis 11:30 Uhr',
        'mittags 11:30–13:30 Uhr',
        'nachmittags bis 18 Uhr',
        'abends 18–21 Uhr',
    ],
};
/** „Label: Wert · Label: Wert" — the catalog value of `leadAnswers` (also built by `contact_profile_values()`). */
export function formatLeadAnswers(entries) {
    if (entries.length === 0)
        return null;
    return entries.map((entry) => `${entry.label}: ${entry.value}`).join(' · ');
}
/** Answer to a setter's change of a financial value (owner decision 12.10.2026: setters read, never edit). */
export const CONTACT_PROFILE_READ_ONLY_MESSAGE = 'Finanzangaben dürfen Sie nur lesen.';
// --- validation of manual edits ------------------------------------------------------------------
const AMOUNT_MESSAGE = 'Bitte geben Sie einen Betrag ab 0 € an.';
const INTEGER_MESSAGE = 'Bitte geben Sie eine ganze Zahl ab 0 an.';
const DATE_MESSAGE = 'Bitte ein Datum im Format JJJJ-MM-TT angeben.';
const TAX_RATE_MESSAGE = 'Bitte geben Sie einen Steuersatz zwischen 0 und 60 % an.';
const OPTION_MESSAGE = 'Bitte wählen Sie einen der vorgegebenen Werte.';
const TEXT_MESSAGE = 'Bitte geben Sie höchstens 200 Zeichen an.';
/** '' and whitespace become null (the field is cleared). */
const textValue = z
    .string({ error: TEXT_MESSAGE })
    .trim()
    .max(200, { error: TEXT_MESSAGE })
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));
const requiredName = (message) => z.string({ error: message }).trim().min(1, { error: message }).max(100, { error: TEXT_MESSAGE });
const centsValue = z
    .number({ error: AMOUNT_MESSAGE })
    .int({ error: AMOUNT_MESSAGE })
    .min(0, { error: AMOUNT_MESSAGE })
    .max(Number.MAX_SAFE_INTEGER, { error: AMOUNT_MESSAGE })
    .nullable();
const integerValue = (max) => z
    .number({ error: INTEGER_MESSAGE })
    .int({ error: INTEGER_MESSAGE })
    .min(0, { error: INTEGER_MESSAGE })
    .max(max, { error: INTEGER_MESSAGE })
    .nullable();
/** „Persönlicher Steuersatz": 0–60 %, at most three decimals (`numeric(6,3)`). */
const taxRateValue = z
    .number({ error: TAX_RATE_MESSAGE })
    .min(0, { error: TAX_RATE_MESSAGE })
    .max(60, { error: TAX_RATE_MESSAGE })
    .refine((value) => Math.abs(Math.round(value * 1000) - value * 1000) < 1e-6, { error: TAX_RATE_MESSAGE })
    .nullable();
const dateValue = z
    .string({ error: DATE_MESSAGE })
    .regex(/^\d{4}-\d{2}-\d{2}$/, { error: DATE_MESSAGE })
    .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), { error: DATE_MESSAGE })
    .nullable();
const enumValue = (values) => z.enum(values, { error: OPTION_MESSAGE }).nullable();
export const updateContactProfileBodySchema = z
    .object({
    salutation: textValue,
    firstName: requiredName('Bitte geben Sie einen Vornamen an.'),
    lastName: requiredName('Bitte geben Sie einen Nachnamen an.'),
    bornOn: dateValue,
    street: textValue,
    postalCode: textValue,
    city: textValue,
    phone: textValue,
    email: z
        .string({ error: 'Bitte geben Sie eine gültige E-Mail-Adresse an.' })
        .trim()
        .max(200, { error: TEXT_MESSAGE })
        .nullable()
        .transform((value) => (value === null || value === '' ? null : value))
        .refine((value) => value === null || z.email().safeParse(value).success, {
        error: 'Bitte geben Sie eine gültige E-Mail-Adresse an.',
    }),
    maritalStatus: enumValue(selfDisclosureMaritalStatuses),
    children: integerValue(20),
    nationality: textValue,
    employment: enumValue(selfDisclosureEmployments),
    employer: textValue,
    employedSinceOn: dateValue,
    contract: enumValue(selfDisclosureContracts),
    fixedTermUntilOn: dateValue,
    netIncomeCents: centsValue,
    salariesPerYear: integerValue(24),
    specialPaymentsPaCents: centsValue,
    otherIncomeCents: centsValue,
    otherIncomeType: enumValue(selfDisclosureOtherIncomeTypes),
    rentalIncomeCents: centsValue,
    taxRatePct: taxRateValue,
    housingSituation: enumValue(selfDisclosureHousingSituations),
    grossRentCents: centsValue,
    rentContinues: z.boolean({ error: 'Bitte wählen Sie ja oder nein.' }).nullable(),
    loansCents: centsValue,
    loansRemainingTerm: textValue,
    alimonyCents: centsValue,
    privateHealthInsuranceCents: centsValue,
    leasingCents: centsValue,
    equityCents: centsValue,
    securitiesCents: centsValue,
    otherAssetsCents: centsValue,
    leadIncomeBand: textValue,
    leadEquityAvailability: textValue,
    leadEquityBand: textValue,
    reachability: textValue,
})
    .partial()
    .strict();
// --- formatting ----------------------------------------------------------------------------------
export const CONTACT_PROFILE_EMPTY_VALUE = '—';
/** Plain text of a raw value that does not match its field type (shown as stored). */
function rawText(value) {
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint')
        return String(value);
    return JSON.stringify(value);
}
/** The display value of one property: null → „—", cents → „4.980 €", boolean → „ja"/„nein", enum → label. */
export function formatContactProfileValue(entry, value) {
    if (value === null || value === undefined || value === '')
        return CONTACT_PROFILE_EMPTY_VALUE;
    switch (entry.type) {
        case 'cents':
            return typeof value === 'number' || typeof value === 'bigint' ? formatEuro(value) : rawText(value);
        case 'integer':
            return typeof value === 'number' ? formatInteger(value) : rawText(value);
        case 'percent':
            return typeof value === 'number' ? formatPercent(value) : rawText(value);
        case 'boolean':
            return value === true ? 'ja' : value === false ? 'nein' : rawText(value);
        case 'date':
            return typeof value === 'string' || value instanceof Date ? formatDate(value) : rawText(value);
        case 'enum': {
            const option = entry.options?.find((candidate) => candidate.value === value);
            return option ? option.label : rawText(value);
        }
        case 'text':
            return rawText(value);
    }
}
//# sourceMappingURL=contact-profile.js.map